import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Upload, X, Leaf } from "lucide-react";
import {
  fetchProducts,
  fetchCategories,
  createProduct,
  editProduct,
  deleteProduct,
} from "../../services/productApi";
import { useTranslation } from "react-i18next";
import { toast } from "../../hooks/use-toast";
import ConfirmAlert from "../../components/ui/ConfirmAlert";
import defaultImage from "../../assets/logo.jpeg";
import { useDropzone } from "react-dropzone";
import api from "../../services/api";

const AdminProducts = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [formData, setFormData] = useState({
    name_en: "",
    name_ar: "",

    price: "",
    category: "",

    shortDescription_en: "",
    shortDescription_ar: "",

    fullDescription_en: "",
    fullDescription_ar: "",

    ingredients_en: "",
    ingredients_ar: "",

    usage_en: "",
    usage_ar: "",
  });

  const onDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      toast({
        title: "Invalid file",
        description: "Only JPG/PNG images under 5MB are allowed",
        variant: "destructive",
      });
      return;
    }

    const file = acceptedFiles[0];
    if (file) {
      setImageFile(file);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/jpg": [],
    },
    maxSize: 5 * 1024 * 1024, // 5MB
  });
  // Load categories once
  useEffect(() => {
    fetchCategories().then(setCategories);
  }, []);

  // Load products
  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await fetchProducts();
      setProducts(data);
    } catch (err) {
      setError("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const openAddForm = () => {
    setIsEditMode(false);
    setEditingProductId(null);
    setFormData({
      name_en: "",
      name_ar: "",

      price: "",
      category: categories[0]?.id || "",

      shortDescription_en: "",
      shortDescription_ar: "",

      fullDescription_en: "",
      fullDescription_ar: "",

      ingredients_en: "",
      ingredients_ar: "",

      usage_en: "",
      usage_ar: "",
    });
    setShowAddForm(true);
  };

  const resetFormState = () => {
    setIsEditMode(false);
    setEditingProductId(null);
    setImageFile(null);
    setFormData({
      name_en: "",
      name_ar: "",

      price: "",
      category: categories[0]?.id || "",

      shortDescription_en: "",
      shortDescription_ar: "",

      fullDescription_en: "",
      fullDescription_ar: "",

      ingredients_en: "",
      ingredients_ar: "",

      usage_en: "",
      usage_ar: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const form = new FormData();

      // ⭐ append PUT only in edit mode
      if (isEditMode) {
        form.append("_method", "PUT");
      }

      form.append("name_en", formData.name_en);
      form.append("name_ar", formData.name_ar);
      // Price logic: empty string → send empty, backend converts to null
      if (formData.price === "") {
        form.append("price", ""); // send empty string
      } else {
        form.append("price", Number(formData.price));
      }
      form.append("category", formData.category);

      form.append("shortDescription_en", formData.shortDescription_en);
      form.append("shortDescription_ar", formData.shortDescription_ar);

      form.append("fullDescription_en", formData.fullDescription_en);
      form.append("fullDescription_ar", formData.fullDescription_ar);

      form.append("ingredients_en", formData.ingredients_en);
      form.append("ingredients_ar", formData.ingredients_ar);

      form.append("usage_en", formData.usage_en);
      form.append("usage_ar", formData.usage_ar);

      if (imageFile) {
        form.append("image", imageFile);
      }
      let savedProduct;

      if (isEditMode) {
        await editProduct(editingProductId, form);

        await loadProducts();

        toast({
          title: t("admin.products.updateSuccess"),
        });
      } else {
        savedProduct = await createProduct(form);
        setProducts((prev) => [savedProduct, ...prev]);
        await loadProducts();
        toast({
          title: t("admin.products.addSuccess"),
        });
      }

      resetFormState();
      setShowAddForm(false);
    } catch (err) {
      toast({
        title: t("admin.errorTitle"),
        description: err.message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (product) => {
    setIsEditMode(true);
    setEditingProductId(product.id);

    const categoryId = product.category?.id || categories[0]?.id || "";

    setFormData({
      name_en: product.name_en,
      name_ar: product.name_ar,

      price: product.price,
      category: categoryId,

      shortDescription_en: product.shortDescription_en,
      shortDescription_ar: product.shortDescription_ar,

      fullDescription_en: product.fullDescription_en,
      fullDescription_ar: product.fullDescription_ar,

      ingredients_en: product.ingredients_en,
      ingredients_ar: product.ingredients_ar,

      usage_en: product.usage_en,
      usage_ar: product.usage_ar,
    });

    setShowAddForm(true);
  };

  const requestDelete = (id) => {
    setSelectedUserId(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedUserId) return;
    try {
      await deleteProduct(selectedUserId);
      setProducts((prev) => prev.filter((p) => p.id !== selectedUserId));

      toast({
        title: t("admin.products.deleteSuccess"),
      });

      loadProducts();
    } catch {
      toast({
        title: t("admin.products.deleteError"),
        variant: "destructive",
      });
    } finally {
      setConfirmOpen(false);
      setSelectedUserId(null);
    }
  };

  const updateFeatured = async (id, featured) => {
    try {
      await api.post(`/products/${id}/toggle-featured`, {
        _method: "PUT",
        featured,
      });

      setProducts((prev) =>
        prev.map((p) => (p.id === id ? { ...p, featured } : p)),
      );

      toast({
        title: t("admin.products.updateSuccess"),
      });
    } catch (err) {
      toast({
        title: t("admin.errorTitle"),
        description: err.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-bold mb-2">
            {t("admin.products.title")}
          </h2>
          <p className="text-muted-foreground">
            {t("admin.products.subtitle")}
          </p>
        </div>
        <button
          onClick={openAddForm}
          className="btn-hero flex items-center gap-2 px-5 py-3 text-sm"
        >
          <Plus size={18} />
          <span>{t("admin.products.addButton")}</span>
        </button>
      </div>

      {/* Add Product Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-large max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3>
                {isEditMode
                  ? t("admin.products.editTitle")
                  : t("admin.products.addTitle")}
              </h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="p-2 hover:bg-muted rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t("admin.products.name")} (EN)
                  </label>

                  <input
                    type="text"
                    value={formData.name_en}
                    onChange={(e) =>
                      setFormData({ ...formData, name_en: e.target.value })
                    }
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    placeholder="Product name in English"
                  />
                </div>

                {/* NAME AR */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t("admin.products.name")} (AR)
                  </label>

                  <input
                    type="text"
                    dir="rtl"
                    value={formData.name_ar}
                    onChange={(e) =>
                      setFormData({ ...formData, name_ar: e.target.value })
                    }
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    placeholder="اسم المنتج بالعربية"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t("admin.products.price")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({ ...formData, price: e.target.value })
                    }
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    placeholder={t("admin.products.placeholderPrice")}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("admin.products.category")}
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {lang === "ar" ? category.name_ar : category.name_en}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("admin.products.image")}
                </label>
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                   ${
                     isDragActive
                       ? "border-primary bg-primary/5"
                       : "border-border hover:border-primary"
                   }
                 `}
                >
                  <input {...getInputProps()} />

                  <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />

                  {imageFile ? (
                    <>
                      <p className="text-sm font-medium">{imageFile.name}</p>
                      <img
                        src={URL.createObjectURL(imageFile)}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-lg mx-auto mt-4"
                      />
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">
                        {isDragActive
                          ? t("admin.products.dropImage")
                          : t("admin.products.uploadImage")}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("admin.products.imageFormats")}
                      </p>
                    </>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("admin.products.shortDescription")} (EN)
                </label>

                <input
                  type="text"
                  value={formData.shortDescription_en}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      shortDescription_en: e.target.value,
                    })
                  }
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("admin.products.shortDescription")} (AR)
                </label>

                <input
                  dir="rtl"
                  type="text"
                  value={formData.shortDescription_ar}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      shortDescription_ar: e.target.value,
                    })
                  }
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("admin.products.fullDescription")} (EN)
                </label>

                <textarea
                  rows={4}
                  value={formData.fullDescription_en}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      fullDescription_en: e.target.value,
                    })
                  }
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("admin.products.fullDescription")} (AR)
                </label>

                <textarea
                  dir="rtl"
                  rows={4}
                  value={formData.fullDescription_ar}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      fullDescription_ar: e.target.value,
                    })
                  }
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("admin.products.ingredients")} (EN)
                </label>

                <input
                  type="text"
                  value={formData.ingredients_en}
                  onChange={(e) =>
                    setFormData({ ...formData, ingredients_en: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("admin.products.ingredients")} (AR)
                </label>

                <input
                  dir="rtl"
                  type="text"
                  value={formData.ingredients_ar}
                  onChange={(e) =>
                    setFormData({ ...formData, ingredients_ar: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("admin.products.usage")} (EN)
                </label>

                <textarea
                  rows={3}
                  value={formData.usage_en}
                  onChange={(e) =>
                    setFormData({ ...formData, usage_en: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("admin.products.usage")} (AR)
                </label>

                <textarea
                  dir="rtl"
                  rows={3}
                  value={formData.usage_ar}
                  onChange={(e) =>
                    setFormData({ ...formData, usage_ar: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none"
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    resetFormState();
                    setShowAddForm(false);
                  }}
                  className="flex-1 px-4 py-3 rounded-xl border border-border hover:bg-muted"
                >
                  {t("common.cancel")}
                </button>
                <button type="submit" className="flex-1 btn-hero">
                  {isEditMode
                    ? t("admin.products.updateButton")
                    : t("admin.products.addButton")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="bg-card rounded-2xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          {loading || error || products.length === 0 ? (
            <div className="text-center py-16">
              {loading && <p>{t("admin.products.loading")}</p>}
              {error && <p className="text-red-500">{error}</p>}
              {!loading && !error && products.length === 0 && (
                <>
                  <Leaf className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg text-muted-foreground">
                    {t("admin.products.noProducts")}
                  </p>
                </>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium">
                    {t("admin.products.table.image")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium">
                    {t("admin.products.table.name")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium">
                    {t("admin.products.table.category")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium">
                    {t("admin.products.table.price")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium">
                    {t("admin.products.table.featured")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium">
                    {t("admin.products.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {products.map((product) => (
                  <tr key={product.id} className="hover:bg-muted/30">
                    <td className="px-6 py-4">
                      <img
                        src={product.image || defaultImage}
                        alt={lang === "ar" ? product.name_ar : product.name_en}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      {lang === "ar" ? product.name_ar : product.name_en}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-2.5 py-1 rounded-full text-xs bg-muted">
                        {lang === "ar"
                          ? product.category?.name_ar
                          : product.category?.name_en}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      {product.price ? (
                        `$${Number(product.price).toFixed(2)}`
                      ) : (
                        <span className="text-muted-foreground italic">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={product.featured ? "yes" : "no"}
                        onChange={(e) =>
                          updateFeatured(product.id, e.target.value === "yes")
                        }
                        className={`px-3 py-1.5 rounded-full text-xs font-medium outline-none transition ${
                          product.featured
                            ? "bg-herb-green/10 text-herb-green"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        <option value="yes">{t("common.yes")}</option>
                        <option value="no">{t("common.no")}</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="p-2 hover:bg-muted rounded-lg"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => requestDelete(product.id)}
                          className="p-2 hover:bg-destructive/10 rounded-lg text-destructive"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {confirmOpen && (
            <ConfirmAlert
              open={confirmOpen}
              title={t("admin.products.deleteTitle")}
              description={t("admin.products.deleteConfirm")}
              onConfirm={handleDelete}
              onCancel={() => setConfirmOpen(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
